import { Component, ElementRef, Input, OnChanges, OnDestroy, OnInit, Output, SimpleChanges, ViewChild, ViewEncapsulation, EventEmitter } from '@angular/core';
import videojs from 'video.js';
import Player from 'video.js/dist/types/player';
import { CameraService } from '../../../services/camera.service';
import { Camera, Device, StreamState } from '../models';
import { WebrtcService } from '@services/webrtc.service';
import { WowzaConfig } from '@/Common/wowza-config.model';

@Component({
  selector: 'stream-viewer',
  templateUrl: './stream-viewer.component.html',
  styleUrls: ['./stream-viewer.component.scss'],
  providers: [CameraService],
  encapsulation: ViewEncapsulation.None,
})
export class StreamViewerComponent implements OnChanges, OnInit, OnDestroy {

  options = {
    liveui: true,
    autoplay: true,
    controlBar: {
      pictureInPictureToggle: false
    },
    plugins: {
      reloadSourceOnError: {}
    }
  };

  @ViewChild('target', { static: true }) target: ElementRef;
  @Input() selectedDevice: Device;
  @Input() selectedWowzaConfig: WowzaConfig;
  @Output() close = new EventEmitter();
  cameras: Camera[] = [];
  state: StreamState = StreamState.LoadingCameras;
  player: Player;
  timeout: any;
  
  _selectedCamera: Camera;
  set selectedCamera(value: Camera) {
    this._selectedCamera = value;
    this.log("Selecting camera " + JSON.stringify(value));
    if (value == null) {
      return;
    }
    this.initilizeStreamed(value);
  }

  get selectedCamera(): Camera {
    return this._selectedCamera;
  }

  constructor(
    private cameraService: CameraService, private webrtcService: WebrtcService) {
    this.state = StreamState.Idle;
  }

  ngOnInit(): void {
    this.state = StreamState.LoadingPlayer;

    // Initialize video.js player
    this.player = videojs(this.target?.nativeElement, this.options, () => {
      console.log("Video.js player initialized");
    });

    // Handle player-level errors
    this.player.on('error', () => {
      const playerError = this.player.error();
      console.error("Video.js error:", playerError);
      this.handleError(playerError?.message || "Unknown video.js error");
    });

    // Handle playlist retry errors (if using HLS or DASH)
    const playerTech = this.player.tech({ IWillNotUseThisInPlugins: true });
    playerTech.on('retryplaylist', () => {
      console.warn("Retry playlist triggered");
      this.handleError("Couldn't load playlist");
    });

    // Subscribe to WebRTC stream errors
    this.webrtcService.streamError.subscribe((errMsg: string) => {
      console.error("Stream error received:", errMsg);
      
      if (errMsg.includes("502")) {
        this.handleError("Stream is offline or unavailable.");
      } else {
        this.handleError(errMsg);
      }

    });
  }

  // If the selected device has changed, load the cameras for the new device and start the stream
  ngOnChanges(changes: SimpleChanges) {
    if (changes.selectedDevice && changes.selectedDevice.previousValue != changes.selectedDevice.currentValue) {
      this.state = StreamState.LoadingPlayer;
      this.selectedDevice = changes.selectedDevice.currentValue;
      this.loadCameras(this.selectedDevice.deviceGroupUserId);
      this.webrtcService.stop();
    }
  }

  // If the stream has been closed, clear state and emit the close event
  deselectDevice() {
    this.state = StreamState.Idle;
    this.selectedCamera = null;
    this.selectedDevice = null;
    this.close.emit(null);
    this.webrtcService.stop();
  }

  // Load the cameras for the selected device
  loadCameras(device = this.selectedDevice?.deviceGroupUserId): PromiseLike<any> {

    if (device == null) {
      return;
    }

    this.state = StreamState.LoadingCameras;
    return this.cameraService.getCameras(device)
      .then((data) => {
        this.cameras = data;
        this.selectedCamera = data[0];
      })
      .catch(() => this.handleError("Failed to load cameras"));
  }

  initilizeStreamed(camera: Camera = this.selectedCamera){
    
    this.state = StreamState.LoadingPlayer;
    this.webrtcService.stop();

    // Clear video element
    const video = this.target.nativeElement as HTMLVideoElement;
    video.pause();
    video.srcObject = null;
    video.load(); // force reset

    clearTimeout(this.timeout); // cancel retries if any

    // Wait a bit before starting the new stream (optional but helps sometimes)
    setTimeout(() => {
      this.loadStream(camera);
    }, 300);
  }

  // Get the stream URL for the selected camera and load it into the player
  loadStream(camera: Camera = this.selectedCamera): Promise<void> {

    this.webrtcService.stop();
    this.timeout = null;

    if (!camera) {
      const err = "Camera is not selected";
      console.error(err);
      this.handleError(err);
      return Promise.resolve(); // Not rejecting — handling error centrally
    }
  
    if (!camera.streamName) {
      const err = "streamName is undefined or empty";
      console.error(err);
      this.handleError(err);
      return Promise.resolve();
    }

    console.log("stream Name: ", camera.streamName)
  
    const videoElement = this.target.nativeElement as HTMLVideoElement;
  
    this.selectedWowzaConfig.streamName = camera.streamName;
    console.log("Selected Wowza Config:", this.selectedWowzaConfig);
    this.state = StreamState.LoadingStreamUrl;
    return this.webrtcService.loadStream(videoElement, this.selectedWowzaConfig)
    .then(() => {
      console.log("WebRTC stream started");
      this.state = StreamState.Streaming;
    });
  }
  
  log($event: any) {
    console.log($event);
  }

  handleError(error: string | null = null): void {
    console.error("Camera Stream:", error);
    this.state = StreamState.Error;
    if (this.timeout) {
      clearTimeout(this.timeout); // cancel previous retry
    }

    this.timeout = setTimeout(() => {
      this.timeout = null;
      this.loadStream();
    }, 5000);
  }

  // Dispose the player OnDestroy
  ngOnDestroy() {
    this.webrtcService.stop();
    if (this.player) {
      this.player.dispose();
    }
  }

}